import {
    StyledTtsPlayerMinimal,
    StyledTtsButton,
    StyledTtsButtonIconCircle,
    StyledTtsButtonLabel,
    StyledFloatingTtsControls,
    StyledFloatingTtsButton,
    StyledTtsAudioHidden,
} from '../TtsPlayer/TtsPlayer.styled'
import { TtsPlayerBar } from '../TtsPlayer/TtsPlayerBar'
import { useTheme } from '@emotion/react'
import {
    isFeatureEnabled,
    toFeatureState,
} from '@west-australian-newspapers/etrigan-feature-toggles-client'
import {
    AllEvents,
    AppState,
    Block,
    RenderTargetContext,
    RequiredAccess,
    useToggleState,
} from '@news-mono/web-common'
import React from 'react'
import { useSelector } from 'react-redux'
import { Logger } from '@west-australian-newspapers/typescript-log'
import { GetVideoQueue } from '../../content/Video/PlayerInterface'
import { StyledBlockContent } from '../../publication/ArticleBlockContent/ArticleBlockContent.styled'
import { InlinePositionedContent } from '../../templates/Publication/Publication.props'
import { LazyRegistrationWall } from '../../user-registration/breach/components/RegistrationWall/RegistrationWall.lazy'
import { ArticleBlock } from './ArticleBlock'
import { StyledMobilePaddingContainer } from '../../templates/Publication/TheNightlyPublication/TheNightlyPublication.styled'
import { ConditionalWrapper } from '../../__helpers/ConditionalWrapper'
import { isShorthand } from '../Embed/web/Shorthand'
import { PNArticleBlock } from '../../nextgen-news/perthnow/publication/PNArticleBlock/PNArticleBlock'
import type { TtsWordInParagraph } from '../TtsText'
import type { PresignedUrlResponse } from '../TtsPlayer/types'
import { createPortal } from 'react-dom'
import { IconSpeaker } from '../../icons/IconSpeaker/IconSpeaker'
import { useArticleTts, ARTICLE_CONTENT_ID } from './useArticleTts'

export const articleContentID = ARTICLE_CONTENT_ID
const regWallPosition = 3

export interface ArticleBlockContentProps {
    showInlineVideoTitles?: boolean
    blocks: Block[]
    targetedContentElement?: React.ReactElement<any>
    inlinePositionedContent?: InlinePositionedContent[]
    onEvent: (event: AllEvents) => void
    log: Logger
    publicationPathname?: string
    publicationKind?: string
    adUnitPath: string
    className?: string
    getVideoQueue?: GetVideoQueue
    requiredAccess?: RequiredAccess
    isEventPost?: boolean
    kicker?: string
    ttsPresignedUrls?: PresignedUrlResponse // Optional presigned URLs from SSR
    /** Slot for TTS collapsed button (byline row). When passed, button only renders after slot is ready. */
    ttsSlotElement?: HTMLDivElement | null
    /** Slot for TTS expanded player (below byline). When set, player is portaled here. */
    ttsExpandedSlotElement?: HTMLDivElement | null
}

interface AmpButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    on: string
}
const AmpButton = (props: AmpButtonProps) => <button {...props} />
export const ArticleBlockContent: React.FC<ArticleBlockContentProps> = ({
    blocks,
    targetedContentElement,
    inlinePositionedContent,
    onEvent,
    log,
    publicationPathname,
    showInlineVideoTitles,
    adUnitPath,
    className,
    getVideoQueue,
    requiredAccess,
    publicationKind,
    isEventPost,
    kicker,
    ttsPresignedUrls,
    ttsSlotElement,
    ttsExpandedSlotElement,
}): JSX.Element | null => {
    const toggles = useToggleState()
    const { isLoggedIn, hasAttemptedValidation } = useSelector(
        (state: AppState) => state.authentication,
    )
    const theme = useTheme()

    // Im not sure if the logic for checking to be logged in and whether the app has
    // attemptedValidation should be done here or within the regwall component?
    const hasRegistrationWall =
        isFeatureEnabled(toFeatureState(toggles), 'registration-wall') &&
        !isLoggedIn &&
        requiredAccess?.level === 'registered'

    const isNgnEnabled = isFeatureEnabled(
        toFeatureState(toggles),
        'perthnow-ngn-redesign-master',
    )

    const ContentBlock = isNgnEnabled ? PNArticleBlock : ArticleBlock

    const { renderTarget } = React.useContext(RenderTargetContext)
    const isReadMoreEnabledTwPn = !!(
        isFeatureEnabled(toFeatureState(toggles), 'amp-read-more') &&
        renderTarget === 'amp'
    )

    const isReadMoreEnabledSeven =
        renderTarget === 'amp' &&
        theme.kind === 'sevennews' &&
        !!isFeatureEnabled(
            toFeatureState(toggles),
            '7-news-taboola-read-more-amp-articles',
        )

    const isReadMoreEnabled = isReadMoreEnabledTwPn || isReadMoreEnabledSeven
    const isArticleTtsEnabled = isFeatureEnabled(
        toFeatureState(toggles),
        '7-news-article-tts',
    )

    const {
        canPlayTts,
        isTtsExpanded,
        preprocessedTtsData,
        tts,
        ttsHeaderRef,
        showFloatingTtsControls,
        floatingControlsPosition,
        handleToggleTtsPlayback,
        handleExpandTtsPlayer,
        handleStopTts,
        handleTtsPlayTriggered,
        doesBlockContributeToTts,
    } = useArticleTts(blocks, ttsPresignedUrls, {
        renderTarget,
        isArticleTtsEnabled,
        articleContentID,
    })

    const floatingControlsRef = React.useRef<HTMLDivElement | null>(null)

    if (!blocks) {
        return null
    }

    // Generate content blocks with preprocessed TTS data.
    // IMPORTANT: We must align paragraph->block mapping in the same order as extractTtsText,
    // rather than relying on array indices (which can shift due to non-text blocks, insertions, slicing, etc).
    let ttsParagraphCursor = 0
    const contentBlocks = (
        hasRegistrationWall ? blocks.slice(0, regWallPosition) : blocks
    ).map<JSX.Element | undefined>((block, index) => {
        /**
         * Dev note:
         *
         * If you are modifying the article content blocks make sure they render on
         * Google News RSS feeds as well. The issue we've previously run into was
         * that because the RSS feed renders the entire article content without a
         * router then the web links will cause an error.
         *
         * To test the Google News RSS feed you can run 7 News locally and go to
         * http://localhost:3040/rss?key=wn0at58dv9l5qg64nzwg
         */
        const isShorthandEmbed =
            block.kind === 'embed' && isShorthand(block.link)

        // Flag the main article content for event articles
        const isEventMainContent = publicationKind === 'event' && !isEventPost

        // Consume paragraph data in the same sequence as extractTtsText.
        // Text/subhead consume 1 paragraph; list consumes 1 paragraph per non-empty list item.
        let ttsWordsForBlock: TtsWordInParagraph[] | undefined = undefined
        let ttsListItemWords:
            | Array<TtsWordInParagraph[] | undefined>
            | undefined = undefined
        if (canPlayTts && preprocessedTtsData.length > 0) {
            if (block.kind === 'text' || block.kind === 'subhead') {
                const shouldContribute =
                    typeof (block as any).text === 'string' &&
                    (block as any).text.trim().length > 0
                if (shouldContribute) {
                    const paraData = preprocessedTtsData[ttsParagraphCursor]
                    ttsParagraphCursor += 1
                    if (paraData) {
                        ttsWordsForBlock = paraData.words
                    }
                }
            } else if (
                block.kind === 'ordered-list' ||
                block.kind === 'unordered-list'
            ) {
                const items = Array.isArray((block as any).items)
                    ? (block as any).items
                    : []
                const itemTexts = items
                    .map((item: any) => {
                        if (
                            !item ||
                            item.kind !== 'list-item' ||
                            !Array.isArray(item.blocks)
                        )
                            return ''
                        const parts = item.blocks
                            .map((b: any) => {
                                if (b && (b.kind === 'text' || b.kind === 'subhead')) {
                                    return typeof b.text === 'string' && b.text.trim()
                                        ? b.text.trim()
                                        : ''
                                }
                                return ''
                            })
                            .filter((s: string) => s.length > 0)
                        return parts.join(' ')
                    })
                    .filter((s: string) => s.length > 0)

                if (itemTexts.length > 0) {
                    ttsListItemWords = []
                    for (let i = 0; i < itemTexts.length; i++) {
                        const paraData = preprocessedTtsData[ttsParagraphCursor]
                        ttsParagraphCursor += 1
                        ttsListItemWords.push(paraData ? paraData.words : undefined)
                    }
                }
            } else if (doesBlockContributeToTts(block)) {
                // Keep cursor alignment for any other contributing block types
                ttsParagraphCursor += 1
            }
        }

        const element = (
            <ContentBlock
                block={block}
                onEvent={onEvent}
                log={log}
                adUnitPath={adUnitPath}
                getVideoQueue={getVideoQueue}
                showInlineVideoTitles={showInlineVideoTitles}
                publicationPathname={publicationPathname}
                publicationKind={publicationKind}
                briefPrefix={
                    isEventMainContent && block.kind === 'text' && index === 0
                        ? kicker
                        : undefined
                }
                {...(canPlayTts && isTtsExpanded
                    ? {
                          ttsWords: ttsWordsForBlock,
                          ttsListItemWords,
                          currentWordIndex: tts.currentWordIndex,
                          isPlaying: tts.isPlaying,
                      }
                    : {})}
            />
        )
        return (
            <ConditionalWrapper
                key={index}
                condition={
                    block.kind !== 'inline' &&
                    !isShorthandEmbed &&
                    theme.kind === 'thenightly' &&
                    (publicationKind !== 'event' || isEventPost !== true)
                }
                Wrapper={({ children }) => (
                    <StyledMobilePaddingContainer>
                        {children}
                    </StyledMobilePaddingContainer>
                )}
            >
                {element}
            </ConditionalWrapper>
        )
    })

    if (hasRegistrationWall && hasAttemptedValidation) {
        contentBlocks.push(
            <LazyRegistrationWall
                key={regWallPosition}
                position={regWallPosition}
                onEvent={onEvent}
            />,
        )
    }

    if (!hasRegistrationWall) {
        // Just be aware that if multiple items are passed in with the same position, the last will take precedence
        if (
            inlinePositionedContent !== undefined &&
            inlinePositionedContent.length > 0
        ) {
            inlinePositionedContent.forEach((inline, index) => {
                const keyedInlineElement = React.cloneElement(inline.element, {
                    key: `inlineContentElement-${index}`,
                })

                // if inline.position === undefined it default to 1 as per previous logic, same with -1
                // indexes > conentBlocks just go last
                contentBlocks.splice(
                    inline.position !== undefined && inline.position !== -1
                        ? inline.position
                        : 1,
                    0,
                    keyedInlineElement,
                )
            })
        }

        // Targeted content displays in the middle if content blocks >= 8
        if (targetedContentElement !== undefined) {
            const keyedTargetedElement = React.cloneElement(
                targetedContentElement,
                {
                    key: 'targetedContentElement',
                },
            )

            if (contentBlocks.length >= 8) {
                const halfwayPoint = Math.floor(contentBlocks.length / 2)
                contentBlocks.splice(halfwayPoint, 0, keyedTargetedElement) // Insert the targetedContentElement
            } else {
                // targetedContentElement displays at the end at content blocks ( < 8 )
                contentBlocks.push(keyedTargetedElement)
            }
        }
    }

    // Collapsed: circle (left, 30×30) + shorter pill label (right), byline-style
    const ttsCollapsedButton =
        canPlayTts && !isTtsExpanded ? (
            <StyledTtsButton type="button" onClick={handleExpandTtsPlayer}>
                <StyledTtsButtonIconCircle>
                    <IconSpeaker volume={3} />
                </StyledTtsButtonIconCircle>
                <StyledTtsButtonLabel>Listen to article</StyledTtsButtonLabel>
            </StyledTtsButton>
        ) : null

    // Expanded: custom TTS bar (hidden native audio + TtsPlayerBar)
    const ttsExpandedPlayer =
        canPlayTts && isTtsExpanded ? (
            <StyledTtsPlayerMinimal
                ref={ttsHeaderRef}
                className={className}
            >
                {tts.urls.audioUrl && (
                    <StyledTtsAudioHidden>
                        <audio
                            ref={tts.audioRef}
                            preload="auto"
                            src={tts.urls.audioUrl}
                            onPlay={handleTtsPlayTriggered}
                        />
                    </StyledTtsAudioHidden>
                )}
                <TtsPlayerBar
                    isPlaying={tts.isPlaying}
                    currentTime={tts.currentTime}
                    duration={tts.duration}
                    playbackRate={tts.playbackRate}
                    onPlayPause={handleToggleTtsPlayback}
                    onSeek={tts.seekTo}
                    onPlaybackRateChange={tts.setPlaybackRate}
                    onClose={handleStopTts}
                />
            </StyledTtsPlayerMinimal>
        ) : null

    const styledContent = (
        <StyledBlockContent
            className={className}
            id={articleContentID}
            hasRegistrationWall={hasRegistrationWall}
            isPerthNowNGN={isNgnEnabled}
        >
            {/* TTS collapsed button: portal when slot ready; fallback above content only when slot not used. */}
            {ttsSlotElement != null && ttsCollapsedButton ? (
                createPortal(ttsCollapsedButton, ttsSlotElement)
            ) : ttsSlotElement === undefined && ttsCollapsedButton ? (
                <div style={{ marginBottom: '24px', display: 'flex' }}>
                    {ttsCollapsedButton}
                </div>
            ) : null}
            {/* Expanded player: portal to header slot when provided, else in article flow */}
            {ttsExpandedSlotElement && ttsExpandedPlayer
                ? createPortal(ttsExpandedPlayer, ttsExpandedSlotElement)
                : ttsExpandedPlayer}
            {showFloatingTtsControls ? (
                <StyledFloatingTtsControls
                    ref={floatingControlsRef}
                    top={floatingControlsPosition?.top}
                    left={floatingControlsPosition?.left}
                >
                    <span>Text to speech</span>
                    <StyledFloatingTtsButton onClick={handleToggleTtsPlayback}>
                        {tts.isPlaying ? 'Pause' : 'Resume'}
                    </StyledFloatingTtsButton>
                </StyledFloatingTtsControls>
            ) : null}
            {isReadMoreEnabled && contentBlocks.length > 4 ? (
                <div
                    className="amp-article-body amp_cropped"
                    data-amp-bind-class="amp_expand"
                >
                    {contentBlocks}
                    <div
                        className="amp-read-more-box"
                        data-amp-bind-hidden="hiddenState"
                    >
                        <AmpButton
                            className={`amp-read-more-btn amp-read-more-btn-${theme.kind}`}
                            on="tap:AMP.setState({amp_expand: 'amp-article-body', hiddenState: true})"
                        >
                            READ MORE
                        </AmpButton>
                    </div>
                </div>
            ) : (
                contentBlocks
            )}
        </StyledBlockContent>
    )
    return styledContent
}
ArticleBlockContent.displayName = 'ArticleBlockContent'
